import { lumi } from '@/lib/lumi'
import { useState } from 'react'

interface Bottle {
  _id: string
  name: string
  brand: string
  proof: string
  age: string
  price: string
  availability: string
  description: string
  keywords: string
  creator: string
  createdAt: string
  updatedAt: string
}

export const useBottles = () => {
  const [bottles, setBottles] = useState<Bottle[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)

  const fetchBottles = async (options?: {
    filter?: Record<string, any>
    sort?: Record<string, 1 | -1>
    limit?: number
    skip?: number
  }) => {
    setLoading(true)
    try {
      const { list, total } = await lumi.entities.bottles.list(options)
      setBottles(list)
      setTotal(total)
      console.log('Bottles fetched:', list)
    } catch (error) {
      console.error('Failed to fetch bottles:', error)
    } finally {
      setLoading(false)
    }
  }

  const createBottle = async (bottleData: Omit<Bottle, '_id' | 'creator' | 'createdAt' | 'updatedAt'>) => {
    try {
      const now = new Date().toISOString()
      const newBottle = await lumi.entities.bottles.create({
        ...bottleData,
        creator: 'admin',
        createdAt: now,
        updatedAt: now
      })
      console.log('Bottle created:', newBottle)
      await fetchBottles()
      return newBottle
    } catch (error) {
      console.error('Failed to create bottle:', error)
      throw error
    }
  }

  const updateBottle = async (id: string, updates: Partial<Bottle>) => {
    if (typeof id !== 'string') {
      throw new Error('ID must be a string')
    }
    
    try {
      const updatedBottle = await lumi.entities.bottles.update(id, {
        ...updates,
        updatedAt: new Date().toISOString()
      })
      console.log('Bottle updated:', updatedBottle)
      await fetchBottles()
      return updatedBottle
    } catch (error) {
      console.error('Failed to update bottle:', error)
      throw error
    }
  }

  const deleteBottle = async (id: string) => {
    if (typeof id !== 'string' || id === '[object Object]') {
      throw new Error('Invalid ID: must be a string, not an object')
    }
    
    try {
      await lumi.entities.bottles.delete(id)
      console.log('Bottle deleted:', id)
      await fetchBottles()
    } catch (error) {
      console.error('Failed to delete bottle:', error)
      throw error
    }
  }

  return {
    bottles,
    total,
    loading,
    fetchBottles,
    createBottle,
    updateBottle,
    deleteBottle
  }
}
